import { h } from 'preact';
import { lazy, Suspense } from 'preact/compat';
import { useHtmlLang, useKeyboardEvents, useAllyFeatureListeners } from 'hooks';
import { useContext, useEffect, useState } from 'preact/hooks';
import { sendPostMessage } from 'helpers/post-message.helper';
import { ApplicationContext, useApplicationSetup } from 'contexts/use-application-setup';
import { RemediationsProvider } from 'contexts/use-remediations';
import { stateStore } from 'global/state.store';
import { useConnect } from 'helpers/use-connect-to-global-store';
import { loadDeferredJs } from 'helpers/scripts-loader';
import { INITIAL_PAGE_STATE, LOGIN_STATE } from 'constants/constants';
import { configStore } from 'global/config.store';
import { loadDeferredCss } from 'helpers/styles-loader';
import { sessionStore } from 'global/session.store';
import { StatisticsProvider } from 'contexts/use-statistics';
import { ReportView } from 'components/report/report';
import { FocusedElementsHandlerWrapper } from 'contexts/use-focused-element-reader';
import { ViolationsProvider } from 'contexts/use-violations';
import { Trigger } from 'types/trigger';
import { Login } from '../login/login';
import { HowItWorksPage } from '../how-it-works/how-it-works-page';
import { About } from '../about/about';
import {
  AllyFeaturesContext,
  AllyFeaturesProvider,
} from '../accessibility-features/use-ally-features';
import { useReaderGuidePosition } from '../accessibility-features/use-reader-guide-position';
import { Skeleton } from '../skeleton/skeleton';
import { MainWrapper } from '../main-wrapper/main-wrapper';
import { AccessibilityFeaturesProvider } from '../accessibility-features/use-accessibility-fetures';
import { LSTProvider } from '../../contexts/lst/lst-provider';
import { InitialPageSections } from './initial-page-sections';
import { InitialPageLst } from './initial-page-lst';
import { getInitialPageView } from './get-initial-page-view';
import { ManageSkeleton } from '../manage/manage-skeleton';
import { Views } from './types';
import './initial-page.scss';

const ManageLazy: any = lazy(() => import('../manage/manage').then((m) => ({ default: m.Manage })));

declare const applicationConfig: { staticVersion: string };

export const InitialPageWrapper = () => {
  const [isAngularLoaded, setIsAngularLoaded] = useState(false);
  const [page, setPage] = useConnect(stateStore.state);
  const [isLogged] = useConnect(sessionStore.isLogged$);
  const { allyFeaturesState } = useContext(AllyFeaturesContext);

  useHtmlLang();

  const config = useApplicationSetup();
  useAllyFeatureListeners(config.sendEvent, config.settings.current);
  useKeyboardEvents(config);
  useReaderGuidePosition(allyFeaturesState);

  const trigger = config?.trigger;
  const isConfigLoaded = config?.tuningsFromServerInProgress === false;

  const view = getInitialPageView({ page, trigger, isAngularLoaded, isConfigLoaded, isLogged });

  useEffect(() => {
    if (page !== INITIAL_PAGE_STATE && trigger === Trigger.Lst) {
      // we don't support login/manage screens for lst widget
      stateStore.state.next(INITIAL_PAGE_STATE);
    }
  }, [page, trigger]);

  // react on config updates and pass new values to the configStore
  useEffect(() => {
    configStore.config$.next(config);
  }, [config]);

  // This hook triggers only once peg widget initialization
  useEffect(() => {
    const unsubscribe = configStore.config$.subscribe((config) => {
      if (!config.tuningsFromServerInProgress) {
        const initialState = config.widgetLayout === 'headless' ? LOGIN_STATE : INITIAL_PAGE_STATE;
        setPage(initialState);
        sendPostMessage({ action: 'appConfigLoaded' });
        unsubscribe();
      }
    });
  }, []);

  useEffect(() => {
    const extendFilePath = (filePath) =>
      // eslint-disable-next-line
      '__CDN__' + `${filePath}?v=${applicationConfig.staticVersion}`;

    if ((view === Views.SkeletonPage || isLogged) && !isAngularLoaded) {
      const appStyles = extendFilePath('frontend/stylesheets/combined.css');
      const mainJs = extendFilePath('frontend/javascripts/combined.js');

      Promise.all([loadDeferredCss(appStyles), loadDeferredJs(mainJs)]).then(() => {
        setIsAngularLoaded(true);
      });
    }
  }, [view, isLogged, isAngularLoaded]);

  return (
    <ApplicationContext.Provider value={config}>
      <LSTProvider>
        <StatisticsProvider>
          <AccessibilityFeaturesProvider>
            <ViolationsProvider>
              <MainWrapper>
                <FocusedElementsHandlerWrapper>
                  {view === Views.SkeletonPage && <Skeleton />}
                  {view === Views.InitialPage && <InitialPageSections />}
                  {view === Views.InitialPageLst && <InitialPageLst />}
                  {view === Views.ReportPage && <ReportView />}
                  {view === Views.AboutPage && <About />}
                  {view === Views.HowItWorksPage && <HowItWorksPage />}
                  {view === Views.Login && <Login />}
                  {view === Views.Manage && (
                    <RemediationsProvider>
                      <Suspense key="manage" fallback={<ManageSkeleton withWCAGSummary />}>
                        <ManageLazy />
                      </Suspense>
                    </RemediationsProvider>
                  )}
                  {/* Angular view */}
                  <div ui-view="" />
                  {/* ------------ */}
                </FocusedElementsHandlerWrapper>
              </MainWrapper>
            </ViolationsProvider>
          </AccessibilityFeaturesProvider>
        </StatisticsProvider>
      </LSTProvider>
    </ApplicationContext.Provider>
  );
};

export const InitialPage = () => (
  <AllyFeaturesProvider>
    <InitialPageWrapper />
  </AllyFeaturesProvider>
);
